import React, { useState } from "react";

/** The state of a pane swap, as the component that draws the panes sees it. */
export interface IPaneSwap<TDetail> {
  /** The key whose pane is the current one. */
  shownKey: string;
  /** The key whose pane is still leaving, or null when nothing is leaving. */
  leaverKey: string | null;
  /** False until the first change: a first pane never plays an entrance. */
  hasSwapped: boolean;
  /** What `describeSwap` returned for the swap being played, kept until the next one starts. */
  detail: TDetail | null;
  /** Put this on the leaving element's `onAnimationEnd` — an HTML element or an SVG group. */
  onLeaverAnimationEnd: (event: React.AnimationEvent<Element>) => void;
}

interface IPaneSwapState<TDetail> {
  shownKey: string;
  leaverKey: string | null;
  hasSwapped: boolean;
  detail: TDetail | null;
}

/**
 * One pane replacing another: while the new pane arrives, the old one stays mounted and leaving
 * until its own exit animation ends. Shared by `KuikaTabContent` (a tab's pane), `KModalStepBody`
 * (a step of a multi-step modal) and `KuikaStatusGlyph` (a status shape), which each draw and
 * animate the two panes themselves — this only decides which key is shown, which is leaving, and
 * when the leaver goes.
 *
 * The swap is derived while rendering, not in an effect: React re-runs this render with the new
 * state before it commits, so the old pane's fiber is still there to become the leaver (matched by
 * its key) instead of being unmounted and mounted again — which would rerun its mount effects and
 * throw away its state. An effect would commit the pane-less render first.
 *
 * - Re-rendering with the same key changes nothing; the current pane updates in place.
 * - One leaver at most: switching again mid-leave hands the leaver role to the pane shown until
 *   then, and the older leaver is dropped at once.
 * - `isEnabled` false (reduced motion) leaves no leaver at all: the new pane replaces the old one.
 * - `describeSwap` is asked, at the moment of the swap, for whatever the caller must remember about
 *   it for as long as it is played — the direction the tabs moved, the node the old step rendered.
 *   It is called during render, so it must not have side effects.
 */
export const usePaneSwap = <TDetail>(
  activeKey: string,
  isEnabled: boolean,
  describeSwap: (fromKey: string, toKey: string) => TDetail
): IPaneSwap<TDetail> => {
  const [swap, setSwap] = useState<IPaneSwapState<TDetail>>({
    shownKey: activeKey,
    leaverKey: null,
    hasSwapped: false,
    detail: null
  });

  let current: IPaneSwapState<TDetail> = swap;
  if (swap.shownKey !== activeKey) {
    current = {
      shownKey: activeKey,
      leaverKey: isEnabled ? swap.shownKey : null,
      hasSwapped: true,
      detail: describeSwap(swap.shownKey, activeKey)
    };
    setSwap(current);
  }

  const onLeaverAnimationEnd = (event: React.AnimationEvent<Element>): void => {
    // Animations inside the pane bubble here too; only the pane's own exit ends it.
    if (event.target !== event.currentTarget) {
      return;
    }

    setSwap((latest: IPaneSwapState<TDetail>): IPaneSwapState<TDetail> => ({ ...latest, leaverKey: null }));
  };

  return { ...current, onLeaverAnimationEnd };
};
